// ─── ChartPopup ───────────────────────────────────────────────────────────────
// Modal that graphs the Visualize table data. Driven by the chart-type icon
// selected in the toolbar. Multiple selected parameters render as multiple
// traces on one chart. Reuses the already-fetched `series` from useVisualizeData.
//
// Header carries a 3-dot menu (matching the latest XD): Chart Settings,
// Quick Report. "Chart Settings" opens a tabbed modal (Y-Axis,
// Titles, Colors, Data) whose values re-render the chart on Save.
// "Quick Report" downloads a PDF (title + chart image).
// ──────────────────────────────────────────────────────────────────────────────

import { useState, useRef, useEffect } from 'react'
import { X, MoreVertical, ChevronDown, Loader } from 'lucide-react'
import {
  ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Label,
  LineChart, Line, ComposedChart, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  RadialBarChart, RadialBar, ReferenceArea,
} from 'recharts'
import WindRoseChart from '../WindRoseChart'
import { WIND_SPEED_BANDS } from '../DashboardWidgets'
import { ChartHoverTooltip } from '../ChartHoverTooltip'
import { onPrimed, primeProject, readOverviewCombine } from '../../../lib/widgetConfigStore'

// 16-point compass, 22.5° per sector, sector 0 centred on North — matches
// the sector layout /api/data/wind-rose/ (and WindRoseChart) expects.
const COMPASS_16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW']
// Upper bound (exclusive) of each speed band except the last, matching
// WIND_SPEED_BANDS' labels ('< 0.5', '0.5–2', '2–4', '4–6', '6–8', '8–10', '> 10').
const WIND_SPEED_THRESHOLDS = [0.5, 2, 4, 6, 8, 10]

const COLORS = ['#358ECD', '#E36B6B', '#5EA64D', '#E0A030', '#8E5EA6', '#3FA7A0', '#C0567B', '#7A8B99']

// Point-shape support — matches the report's chart editor (DashboardWidgets).
const POINT_SHAPES = ['Circle', 'Square', 'Diamond', 'Triangle', 'None']

// Recharts `dot` for a given shape + colour. Circle uses the built-in dot
// config; the others draw a custom SVG marker at each point.
function makeDot(shape, color) {
  if (shape === 'None') return false
  if (!shape || shape === 'Circle') return { r: 2, fill: color, stroke: color }
  return (props) => {
    const { cx, cy } = props
    if (cx == null || cy == null) return null
    const s = 3, k = `${cx}-${cy}`
    if (shape === 'Square')   return <rect key={k} x={cx - s} y={cy - s} width={s * 2} height={s * 2} fill={color} />
    if (shape === 'Diamond')  return <path key={k} d={`M${cx},${cy - s} L${cx + s},${cy} L${cx},${cy + s} L${cx - s},${cy} Z`} fill={color} />
    if (shape === 'Triangle') return <path key={k} d={`M${cx},${cy - s} L${cx + s},${cy + s} L${cx - s},${cy + s} Z`} fill={color} />
    return <circle key={k} cx={cx} cy={cy} r={2} fill={color} />
  }
}

// Small swatch drawn in the chosen shape + colour, shown beside the colour picker.
function ShapeSwatch({ shape, color }) {
  const c = 8, s = 5
  return (
    <svg width="22" height="22" viewBox="0 0 16 16" aria-hidden="true">
      {shape === 'Square'   && <rect x={c - s} y={c - s} width={s * 2} height={s * 2} fill={color} />}
      {shape === 'Diamond'  && <path d={`M${c},${c - s} L${c + s},${c} L${c},${c + s} L${c - s},${c} Z`} fill={color} />}
      {shape === 'Triangle' && <path d={`M${c},${c - s} L${c + s},${c + s} L${c - s},${c + s} Z`} fill={color} />}
      {(shape === 'Circle' || shape === 'None' || !shape) &&
        <circle cx={c} cy={c} r={s} fill={shape === 'None' ? 'white' : color} stroke={color} />}
    </svg>
  )
}

const pad = (n) => String(n).padStart(2, '0')
// Shift UTC ms by the account's tz offset before reading date/time parts,
// matching exactly how the spreadsheet grid labels are built in useVisualizeData.
const fmt = (ms, offsetSeconds = 0) => {
  const d = new Date(ms + offsetSeconds * 1000)
  return `${pad(d.getUTCMonth() + 1)}/${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

const DEFAULT_SETTINGS = {
  chartTitle: '', xTitle: '',
  // Per-series display config, keyed by series id:
  //   { color, pointShape, yTitle, yMin, yMax, ySteps }
  series: {},
  // Legacy chart-wide fields kept for back-compat with older saved settings
  // (read as a fallback when a series has no per-series value).
  yMin: '', yMax: '', ySteps: '', yTitle: '', colors: {},
  smoothLine: false, smoothMethod: 'moving-average', avgWindow: 5,
  removeSpikes: false, stdDev: 2,
}

// Persist chart settings across refreshes (localStorage — no backend/DB needed).
const SETTINGS_KEY = 'wqdata_da_chart_settings_v1'
function loadSettings() {
  try {
    const s = JSON.parse(localStorage.getItem(SETTINGS_KEY))
    return s ? { ...DEFAULT_SETTINGS, ...s } : DEFAULT_SETTINGS
  } catch { return DEFAULT_SETTINGS }
}
function saveSettings(s) {
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(s)) } catch { /* ignore quota */ }
}

// ── data transforms driven by Chart Settings → Data tab ──
function mean(arr) { return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0 }
function stdDev(arr, m) { return arr.length ? Math.sqrt(mean(arr.map((v) => (v - m) ** 2))) : 0 }

function applyDataSettings(series, s) {
  return series.map((ser) => {
    let pts = ser.points
    if (s.removeSpikes && pts.length > 2) {
      // Null points are hidden-status gaps — exclude them from the stats and
      // keep them (so the line still breaks there), filter only real spikes.
      const ys = pts.map((p) => p.y).filter((v) => v != null)
      const m = mean(ys), sd = stdDev(ys, m), k = Number(s.stdDev) || 2
      if (sd > 0) pts = pts.filter((p) => p.y == null || Math.abs(p.y - m) <= k * sd)
    }
    if (s.smoothLine) {
      const w = Math.max(1, Number(s.avgWindow) || 1)
      pts = pts.map((p, i) => {
        if (p.y == null) return { x: p.x, y: null }   // preserve the gap
        const lo = Math.max(0, i - Math.floor(w / 2))
        const win = pts.slice(lo, lo + w).map((q) => q.y).filter((v) => v != null)
        return { x: p.x, y: win.length ? mean(win) : null }
      })
    }
    return { ...ser, points: pts }
  })
}

function toRows(series, offsetSeconds = 0) {
  const map = new Map()
  series.forEach((s) => {
    s.points.forEach((pt) => {
      const row = map.get(pt.x) || { x: pt.x, label: fmt(pt.x, offsetSeconds) }
      row[`p${s.id}`] = pt.y
      map.set(pt.x, row)
    })
  })
  return [...map.values()].sort((a, b) => a.x - b.x)
}

export default function ChartPopup({
  open, onClose, chartType, series = [], loading = false, tzOffset = 0, customerTz = 'UTC',
  // D/W/M/Y — drives the Multi-Line chart's X-axis tick density/format
  // (see multiXTicks/multiXTickFormatter below). onPeriodChange lets the user
  // switch period from inside the popup (the toolbar's D/W/M/Y buttons sit
  // behind the modal overlay) so the X/Y axes update without closing it.
  activePeriod = 'D', onPeriodChange,
  // Wind Rose plots an independently-picked speed/direction parameter pair
  // (chosen via WindRoseEditorModal in VisualizeTab, not the sidebar's
  // arbitrary multi-select) — these props carry that fetched data down.
  windRoseSeries = null, windRoseLoading = false, windRoseWidgetName = '',
  windRoseKeyColors = null, onEditWindRose,
  // Project id so we can carry the Overview dashboard's Y-Axis combine picks
  // over to this chart (see readOverviewCombine / axisGroups below).
  projectId = null,
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [settings, setSettings] = useState(loadSettings)
  // On a phone a 16:9 dialog is short (width-driven), so the chart comes out
  // tiny. Use a near-full-screen portrait size on mobile instead (matches the
  // XD); desktop keeps the 16:9 dialog.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 767px)').matches
      : false
  )
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(max-width: 767px)')
    const onChange = (e) => setIsMobile(e.matches)
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange) }
  }, [])
  // Resolve the project id bullet-proof: prop first, else parse it straight out
  // of the URL (/dashboard/project/<id>). useParams() has been coming back empty
  // for this tab, which left projectId null and broke the Overview combine
  // carry-over, so we never depend on it alone.
  const effProjectId = (projectId != null && projectId !== '')
    ? projectId
    : ((typeof window !== 'undefined' && window.location.pathname.match(/\/project\/(\d+)/)?.[1]) || null)
  // The Overview Y-Axis combine lives in the server-synced widget-config cache,
  // which is primed asynchronously on dashboard mount. If the chart renders
  // before that fetch lands, readOverviewCombine() sees an empty cache. Prime
  // it ourselves (idempotent — no-op if already primed) and re-render once the
  // prime completes so the combine carries over without needing a reload.
  const [, setPrimeTick] = useState(0)
  useEffect(() => {
    if (effProjectId) primeProject(effProjectId)
    const off = onPrimed(() => setPrimeTick((t) => t + 1))
    return off
  }, [effProjectId])
  const [busy, setBusy] = useState('')
  const chartRef = useRef(null)
  const [zoomDragStart, setZoomDragStart] = useState(null)
  const [zoomDragEnd,   setZoomDragEnd]   = useState(null)
  const [zoomDomain,    setZoomDomain]    = useState(null)
  // Legend hover-to-highlight: the parameter id hovered in the legend. Its line
  // is emphasised and the others dimmed (see the Line map below).
  const [hoveredParamId, setHoveredParamId] = useState(null)
  // Legend click-to-toggle: parameter ids the user has hidden. A hidden series
  // is dropped from the plot (its line/area/bar and, on the multi-axis line
  // chart, its Y-axis) while its legend entry stays (struck-through / greyed)
  // so it can be clicked again to restore it — same behaviour as the Overview
  // line chart, so the user picks exactly which parameters to view.
  const [hiddenIds, setHiddenIds] = useState(() => new Set())
  const toggleHidden = (id) => setHiddenIds((prev) => {
    const next = new Set(prev)
    const key = String(id)
    if (next.has(key)) next.delete(key); else next.add(key)
    return next
  })
  const isHidden = (id) => hiddenIds.has(String(id))
  // Recharts legend payload carries the series dataKey ("p<id>"); strip the
  // "p" prefix to recover the parameter id.
  const legendId = (o) => { const dk = o?.dataKey; return dk == null ? null : String(dk).replace(/^p/, '') }
  // Shared Legend props: spread into every chart type's <Legend> so click
  // (toggle), hover (highlight), and the struck-through hidden style all behave
  // identically across Line / Area / Column / Bar / Combo charts.
  const legendProps = {
    wrapperStyle: { cursor: 'pointer' },
    onClick: (o) => { const id = legendId(o); if (id) toggleHidden(id) },
    onMouseEnter: (o) => { const id = legendId(o); if (id && !isHidden(id)) setHoveredParamId(id) },
    onMouseLeave: () => setHoveredParamId(null),
    formatter: (value, entry) => {
      const id = legendId(entry)
      return (
        <span style={id != null && isHidden(id) ? { textDecoration: 'line-through', color: '#9ca3af' } : undefined}>
          {value}
        </span>
      )
    },
  }

  if (!open) return null

  // Per-series display config (falls back to legacy chart-wide fields, then
  // the default palette / auto axis).
  const cfgFor = (s, i) => {
    const ss = settings.series?.[s.id] || {}
    return {
      color:  ss.color  || settings.colors?.[s.id] || COLORS[i % COLORS.length],
      shape:  ss.pointShape || 'Circle',
      yTitle: ss.yTitle ?? settings.yTitle ?? '',
      yMin:   ss.yMin   ?? settings.yMin   ?? '',
      yMax:   ss.yMax   ?? settings.yMax   ?? '',
      ySteps: ss.ySteps ?? settings.ySteps ?? '',
    }
  }
  const colorFor = (s, i) => cfgFor(s, i).color
  // Domain + explicit ticks for a series config. Explicit ticks so a configured
  // min/max/steps renders (Recharts otherwise auto-rounds to "nice" values).
  const domainFor = (c) => [c.yMin !== '' ? Number(c.yMin) : 'auto', c.yMax !== '' ? Number(c.yMax) : 'auto']
  const ticksFor = (c) => {
    if (c.yMin === '' || c.yMax === '') return undefined
    const mn = Number(c.yMin), mx = Number(c.yMax)
    if (!(mx > mn)) return undefined
    const steps = c.ySteps !== '' ? Math.max(1, Math.round(Number(c.ySteps))) : 4
    return Array.from({ length: steps + 1 }, (_, i) => Math.round((mn + (mx - mn) * i / steps) * 1000) / 1000)
  }
  const effSeries = applyDataSettings(series, settings)
  // Repeated parameters share one Y axis. Picking the same parameter from two
  // devices used to draw two identical scales flanking the plot; grouping on
  // name + unit collapses them (the same parameter in C and F still gets its
  // own axis, since those scales genuinely differ).
  // A per-series "Y-Axis" override (settings.series[id].yAxisGroup = "1".."4")
  // collapses those parameters onto ONE shared axis regardless of name/unit — so
  // e.g. YSI Turbidity (NTU) and NexSens Turbidity (FNU) can be compared on a
  // single scale. "Auto"/blank keeps the unit-name default.
  //
  // Carry-over: when the user hasn't set a combine HERE, we fall back to the
  // combine they made on the Overview dashboard's Line Chart for this project,
  // so the same grouping shows up in Data Analytics automatically. The chart's
  // own Chart Settings → Combine still overrides per series.
  // Plain call (not useMemo): this runs below the `if (!open) return null`
  // guard, so it must not be a hook. It's a cheap localStorage scan.
  const overviewCombine = readOverviewCombine(effProjectId)
  const axisGroups = (() => {
    const groups = []
    const byKey = new Map()
    effSeries.forEach((s, i) => {
      const own = String(settings.series?.[s.id]?.yAxisGroup ?? '').trim()
      const override = (own && own.toLowerCase() !== 'auto')
        ? own
        : String(overviewCombine[String(s.id)] ?? '').trim()
      // Group by unit name (matches the legacy Symfony V2 report graph); blank
      // unit keeps its own axis.
      const unit = String(s.unit ?? '').trim().toLowerCase()
      const key = (override && override.toLowerCase() !== 'auto')
        ? `grp:${override.toLowerCase()}`
        : (unit ? `unit:${unit}` : `p:${s.id}`)
      let g = byKey.get(key)
      if (!g) { g = { key, axisId: `axis-${key}`, members: [], firstIdx: i }; byKey.set(key, g); groups.push(g) }
      g.members.push({ s, i })
    })
    return groups
  })()
  const axisIdOf = (id) => {
    const g = axisGroups.find((grp) => grp.members.some((m) => String(m.s.id) === String(id)))
    return g ? g.axisId : `axis-${id}`
  }
  const hasData = effSeries.some((s) => s.points.some((p) => p.y != null))
  // Only render chart elements (axes, lines, legend entries) for series that
  // actually have data points. Empty series produce phantom Y-axes and legend
  // entries but no visible line.
  const seriesWithData = effSeries.filter((s) => s.points.some((p) => p.y != null))
  const rows = toRows(effSeries, tzOffset)
  // Point shapes are drawn at every reading only in D view; over W/M/Y ranges
  // they'd crowd the chart, so the shape appears on hover (activeDot) instead.

  const isPie = ['pie', 'donut', 'half-pie'].includes(chartType)
  const isArea = chartType === 'area'
  const isBar = ['bar', 'column', 'column-grp', 'h-bar'].includes(chartType)
  const isWindRose = chartType === 'wind-rose'
  const isGauge = chartType === 'gauge'
  const isMulti = chartType === 'multi-line'
  const isCombo = chartType === 'combo'
  const horizontal = chartType === 'h-bar'

  // Charts with a single shared y-axis (area/bar) use the first series'
  // axis config; the multi-axis line chart uses each series' own (below).
  const sharedCfg = effSeries.length ? cfgFor(effSeries[0], 0) : { yMin: '', yMax: '', ySteps: '', yTitle: '' }
  const yDomain = domainFor(sharedCfg)
  const yTicks  = ticksFor(sharedCfg)
  const lineType = settings.smoothLine ? 'monotone' : 'linear'

  const pieData = seriesWithData.map((s, i) => ({
    name: s.unit ? `${s.name} (${s.unit})` : s.name,
    value: (() => { const ys = s.points.map((p) => p.y).filter((v) => v != null); return ys.length ? mean(ys) : 0 })(),
    fill: colorFor(s, i),
  }))
  // Join speed + direction points by exact timestamp (same join key every
  // other multi-series view in this file uses — see toRows above), then
  // bucket each paired observation into a 16-compass-sector × 7-speed-band
  // grid for WindRoseChart. `windRoseSeries` is the pair the user picked
  // explicitly in WindRoseEditorModal (VisualizeTab) — independent of
  // whatever's checked in the Site panel, since a wind rose needs an exact
  // speed+direction pair the sidebar's arbitrary multi-select can't guarantee.
  const windRoseData = (() => {
    if (!isWindRose || !windRoseSeries?.speed || !windRoseSeries?.direction) return null
    const speedSeries = windRoseSeries.speed
    const dirSeries = windRoseSeries.direction
    const dirByX = new Map(dirSeries.points.map((p) => [p.x, p.y]))
    const sectors = COMPASS_16.map((direction, i) => ({ direction, deg: i * 22.5, bins: new Array(WIND_SPEED_BANDS.length).fill(0) }))
    let minX = null, maxX = null
    speedSeries.points.forEach((p) => {
      const dir = dirByX.get(p.x)
      if (dir == null || p.y == null) return
      if (minX == null || p.x < minX) minX = p.x
      if (maxX == null || p.x > maxX) maxX = p.x
      const sectorIdx = Math.round((((Number(dir) % 360) + 360) % 360) / 22.5) % 16
      let bandIdx = WIND_SPEED_THRESHOLDS.length
      for (let i = 0; i < WIND_SPEED_THRESHOLDS.length; i++) {
        if (Number(p.y) < WIND_SPEED_THRESHOLDS[i]) { bandIdx = i; break }
      }
      sectors[sectorIdx].bins[bandIdx] += 1
    })
    // Total paired samples + the sector with the most observations — same
    // summary line the Overview dashboard's Wind Rose widget shows below
    // its chart ("N paired observations · dominant: <sector>").
    let totalPairedSamples = 0, dominantSector = null, dominantTotal = 0
    sectors.forEach((sec) => {
      const total = sec.bins.reduce((a, b) => a + b, 0)
      totalPairedSamples += total
      if (total > dominantTotal) { dominantTotal = total; dominantSector = sec.direction }
    })
    return {
      sectors,
      totalPairedSamples,
      dominantSector,
      fromDate: minX ? new Date(minX).toISOString() : null,
      toDate: maxX ? new Date(maxX).toISOString() : null,
    }
  })()

  const yAxis = (extra = {}) => (
    <YAxis tick={{ fontSize: 11 }} domain={yDomain} allowDataOverflow={true} ticks={yTicks} {...extra}>
      {sharedCfg.yTitle && <Label value={sharedCfg.yTitle} angle={-90} position="insideLeft" style={{ fontSize: 11 }} />}
    </YAxis>
  )
  // Pre-compute which labels fall on day boundaries (day changes from prior tick)
  // and which labels are exactly on a 2-hour boundary (HH % 2 === 0 and MM === 00).
  // Only 2-hour ticks are shown; day-boundary ticks get a "Mon D" date label
  // instead of "HH:MM" (matches XD design).
  const { xDayBoundaries, xTwoHourTicks } = (() => {
    const dayBounds = new Set()
    const twoHour = []
    let prevDay = null
    for (const row of rows) {
      const lbl = row.label || ''
      const day = lbl.slice(0, 5) // "MM/DD"
      const timePart = lbl.slice(6) // "HH:MM"
      const [hh, mm] = timePart.split(':').map(Number)
      if (prevDay !== null && day !== prevDay) dayBounds.add(lbl)
      // Include this tick if it's on an even hour and exactly on the hour
      if (!isNaN(hh) && !isNaN(mm) && hh % 2 === 0 && mm === 0) twoHour.push(lbl)
      prevDay = day
    }
    // If no exact 2-hour ticks exist (e.g. data starts at :30), fall back to
    // showing every other distinct label so the axis is never blank.
    const ticks = twoHour.length ? twoHour : rows.filter((_, i) => i % 2 === 0).map((r) => r.label)
    return { xDayBoundaries: dayBounds, xTwoHourTicks: ticks }
  })()
  const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  const xTickFormatter = (lbl) => {
    if (!lbl) return ''
    if (xDayBoundaries.has(lbl)) {
      const [datePart] = lbl.split(' ')
      const [m, d] = datePart.split('/')
      return `${MONTH_ABBR[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`
    }
    return lbl.slice(6) // "HH:MM"
  }

  // Multi-Line specific X-axis ticks — xTwoHourTicks/xTickFormatter above
  // assume a single-day view (2-hour ticks); over a W/M/Y range that would
  // produce hundreds of ticks. Matches the XD design's period-specific
  // density instead:
  //   D       — unchanged (2-hour ticks, xTwoHourTicks/xTickFormatter above)
  //   W       — 12-hour ticks: noon shows "HH:MM", midnight shows the short date
  //   M/Y     — day-boundary ticks only, spaced out so the total tick count
  //             stays readable (~15 labels) regardless of how many days the
  //             selected range actually spans
  const { multiXTicks, multiXTickFormatter } = (() => {
    const fmtDate = (lbl) => {
      const [m, d] = lbl.slice(0, 5).split('/')
      return `${MONTH_ABBR[parseInt(m, 10) - 1]} ${parseInt(d, 10)}`
    }
    if (activePeriod === 'W') {
      // Tick every 12 hours: midnight shows the date ("Aug 7"), noon shows the
      // time ("12:00") — so a week reads "Aug 7 | 12:00 | Aug 8 | 12:00 | …".
      //
      // Ticks are chosen by PROXIMITY to each 12-hour boundary, not by matching
      // the label text against '00:00'/'12:00'. Exact matching worked only for
      // loggers that sample on the hour; the X2 meta parameters sample at
      // :10/:20/:50, never matched, and fell through to a "every 6th row"
      // fallback — which is what produced an axis of arbitrary raw times
      // ("18:20 | 01:30 | 05:10") with no date anywhere.
      //
      // Each row is assigned to its nearest boundary (so it is never more than
      // 6h away), and the closest row per boundary wins. The label text comes
      // from the BOUNDARY, not the row, so a 23:50 reading standing in for the
      // next midnight still prints that day's date.
      const TWELVE_H = 12 * 3600 * 1000
      const shifted = (x) => x + tzOffset * 1000
      const firstShifted = shifted(rows[0].x)
      const f = new Date(firstShifted)
      // Midnight of the first day, in the same shifted clock as the labels.
      const originMidnight = Date.UTC(f.getUTCFullYear(), f.getUTCMonth(), f.getUTCDate())

      const bestPerBoundary = new Map()   // boundary index → { row, dist }
      for (const row of rows) {
        const s = shifted(row.x)
        const idx = Math.round((s - originMidnight) / TWELVE_H)
        const dist = Math.abs(s - (originMidnight + idx * TWELVE_H))
        const cur = bestPerBoundary.get(idx)
        if (!cur || dist < cur.dist) bestPerBoundary.set(idx, { row, dist })
      }

      const display = new Map()           // row label → text drawn on the axis
      const ticks = []
      for (const idx of [...bestPerBoundary.keys()].sort((a, b) => a - b)) {
        const { row } = bestPerBoundary.get(idx)
        if (display.has(row.label)) continue   // two boundaries, one row
        const b = new Date(originMidnight + idx * TWELVE_H)
        // Even index = midnight (date), odd = noon ("12:00").
        display.set(row.label, idx % 2 === 0
          ? `${MONTH_ABBR[b.getUTCMonth()]} ${b.getUTCDate()}`
          : '12:00')
        ticks.push(row.label)
      }
      return {
        multiXTicks: ticks.length ? ticks : rows.map((r) => r.label),
        multiXTickFormatter: (lbl) => display.get(lbl) ?? '',
      }
    }
    // Total span of the loaded data, in days. A "D" preset or a custom date
    // range can still cover several days; the 2-hour ticks below only stay
    // readable within ~a day, so anything wider falls back to day-spaced ticks
    // (the same nice-step logic as M/Y) instead of packing 50+ labels onto the
    // axis — which is what rendered the x-axis as an unreadable band.
    const spanDays = rows.length > 1
      ? (rows[rows.length - 1].x - rows[0].x) / 86_400_000
      : 0
    if (activePeriod !== 'M' && activePeriod !== 'Y' && spanDays <= 1.5) {
      return { multiXTicks: xTwoHourTicks, multiXTickFormatter: xTickFormatter }
    }
    // Day-boundary ticks, spaced to a "nice" day-step so the total count stays
    // around 15 regardless of the actual span (a few days vs a full year).
    const dayRows = rows.filter((row) => (row.label || '').slice(6) === '00:00')
    const source = dayRows.length ? dayRows : rows
    const totalDays = source.length > 1
      ? Math.max(1, Math.round((source[source.length - 1].x - source[0].x) / 86_400_000))
      : 1
    const NICE_STEPS = [1, 2, 3, 5, 7, 10, 14, 21, 30, 45, 60, 90, 120, 180, 365]
    const targetCount = 15
    const step = NICE_STEPS.find((n) => n >= totalDays / targetCount) || NICE_STEPS[NICE_STEPS.length - 1]
    const ticks = []
    let lastX = null
    for (const row of source) {
      if (lastX === null || (row.x - lastX) >= step * 86_400_000) {
        ticks.push(row.label)
        lastX = row.x
      }
    }
    return { multiXTicks: ticks.length ? ticks : source.map((r) => r.label), multiXTickFormatter: fmtDate }
  })()

  // Tooltip value formatter — the default Recharts tooltip shows the bare
  // number ("Air Temperature : 24.9"); round it to the parameter's configured
  // Precision and append the unit → "Air Temperature : 75.7 F". Keyed by the
  // Line/Bar/Area dataKey ("p<id>").
  const unitByKey = new Map(effSeries.map((s) => [`p${s.id}`, s.unit || '']))
  const precByKey = new Map(effSeries.map((s) => [`p${s.id}`, Number.isFinite(s.precision) ? s.precision : 0]))
  const fmtVal = (value, prec) =>
    (typeof value === 'number' && Number.isFinite(prec)) ? value.toFixed(prec) : value
  const tipFormatter = (value, name, item) => {
    const unit = unitByKey.get(item?.dataKey) || ''
    const v = fmtVal(value, precByKey.get(item?.dataKey))
    return [unit ? `${v} ${unit}` : v, name]
  }

  // Shared XD "data hover box" (see ChartHoverTooltip). `label` is already the
  // pre-binned x label string (e.g. "01/06 11:00").
  const renderHoverTooltip = (p) => (
    <ChartHoverTooltip
      {...p}
      formatLabel={(l) => (l == null ? '' : String(l))}
      describe={(entry) => {
        const dk = entry?.dataKey
        const unit = unitByKey.get(dk) || ''
        const v = fmtVal(entry?.value, precByKey.get(dk))
        return {
          name: entry?.name || dk,
          valueText: unit ? `${v} ${unit}` : `${v}`,
          color: entry?.color || entry?.stroke || entry?.fill,
        }
      }}
    />
  )

  // Period-aware ticks/formatter (D → 2-hour times, W → weekday+day dates,
  // M/Y → spaced date labels) so EVERY chart type's x-axis reads the same way
  // as the Overview tab — not raw times over a multi-day range.
  // NB: no in-axis <Label> for the X title — the charts render with no bottom
  // margin, so an insideBottom Label had no room and never appeared (same issue
  // the Multi-Line chart hit). The title is drawn as a <div> beneath the chart
  // via withXTitle() instead, which renders reliably for every chart type.
  const xAxis = (props = {}) => (
    <XAxis dataKey="label" tick={{ fontSize: 11 }} ticks={multiXTicks} tickFormatter={multiXTickFormatter} {...props} />
  )

  // Wrap a chart so the configured X-axis title shows centred beneath it.
  const withXTitle = (chart) => (
    settings.xTitle
      ? (
        <div className="flex flex-col w-full h-full">
          <div className="flex-1 min-h-0">{chart}</div>
          {/* pt-3/pb-2 gives the title even breathing room from the legend
              (parameter icons) above it instead of sitting cramped against it. */}
          <div className="text-center text-xs font-medium text-gray-600 pt-3 pb-2 shrink-0">{settings.xTitle}</div>
        </div>
      )
      : chart
  )

  const renderChart = () => {
    // While data is loading (initial fetch or a D/W/M/Y switch) show a spinner
    // rather than the stale chart or a premature "No data" — the series briefly
    // empties during the refetch. Wind Rose keeps its own loading branch below.
    if (loading && !isWindRose) {
      return (
        <div className="flex items-center justify-center h-full text-sm text-gray-400 gap-2">
          <Loader className="w-5 h-5 animate-spin text-[#358ECD]" /> Loading data…
        </div>
      )
    }
    // Wind Rose has its own independent data source (windRoseSeries, from
    // WindRoseEditorModal) — it doesn't depend on the Site panel's checked
    // parameters at all, so `hasData` (computed from `series`) must not
    // gate it; its own branch below handles loading/empty states.
    if (!hasData && !isWindRose) return <div className="flex items-center justify-center h-full text-sm text-gray-400">No data to plot for the selected range.</div>
    if (isPie) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy={chartType === 'half-pie' ? '75%' : '50%'}
              startAngle={chartType === 'half-pie' ? 180 : 0} endAngle={chartType === 'half-pie' ? 0 : 360}
              innerRadius={chartType === 'donut' ? 60 : 0} outerRadius={120} label>
              {pieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
            </Pie>
            <Tooltip formatter={tipFormatter} /><Legend />
          </PieChart>
        </ResponsiveContainer>
      )
    }
    if (isArea) {
      return withXTitle(
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={rows}><CartesianGrid strokeDasharray="3 3" />{xAxis()}{yAxis()}<Tooltip content={renderHoverTooltip} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} /><Legend {...legendProps} />
            {seriesWithData.map((s, i) => <Area key={s.id} hide={isHidden(s.id)} type={lineType} dataKey={`p${s.id}`} name={s.name} stroke={colorFor(s, i)} fill={colorFor(s, i)} fillOpacity={0.25} dot={activePeriod === 'D' ? makeDot(cfgFor(s, i).shape, colorFor(s, i)) : false} activeDot={makeDot(cfgFor(s, i).shape, colorFor(s, i))} connectNulls={false} />)}
          </AreaChart>
        </ResponsiveContainer>
      )
    }
    if (isBar) {
      // 'column-grp' is the Stacked Column chart — every series shares one
      // stackId so the bars stack on top of each other. 'column'/'bar'/'h-bar'
      // stay grouped (side-by-side).
      const stacked = chartType === 'column-grp'
      return withXTitle(
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout={horizontal ? 'vertical' : 'horizontal'}><CartesianGrid strokeDasharray="3 3" />
            {/* IMPORTANT: render each axis as a DIRECT child of BarChart, never
                wrapped in a <>…</> Fragment. Recharts locates axes by scanning
                its direct children and does not recurse into Fragments, so a
                fragment-wrapped axis silently disappears (no timestamps, no
                value scale). */}
            {horizontal
              ? <XAxis type="number" tick={{ fontSize: 11 }} domain={yDomain} allowDataOverflow={true} ticks={yTicks} />
              : xAxis()}
            {/* Horizontal bar: category (timestamp) axis on the left. With
                hundreds of points every band is sub-pixel, so thin the ticks
                to ~20 evenly-spaced timestamps to keep them readable. */}
            {horizontal
              ? <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={90} interval={Math.max(0, Math.ceil(rows.length / 20) - 1)} />
              : yAxis()}
            <Tooltip content={renderHoverTooltip} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} /><Legend {...legendProps} />
            {seriesWithData.map((s, i) => <Bar key={s.id} hide={isHidden(s.id)} dataKey={`p${s.id}`} name={s.name} fill={colorFor(s, i)} {...(stacked ? { stackId: 'stack' } : {})} />)}
          </BarChart>
        </ResponsiveContainer>
      )
    }
    if (isCombo) {
      // Combo: every selected parameter is a Bar EXCEPT the last one, which is
      // a Line drawn on top. Shared Y-axis + the same 2-hour X-axis scale as
      // the Line Chart (via the shared xAxis() helper). Rendering the line as
      // the final child keeps it above the bars.
      const lastIdx = seriesWithData.length - 1
      return withXTitle(
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={rows}>
            <CartesianGrid strokeDasharray="3 3" />
            {xAxis()}{yAxis()}
            <Tooltip content={renderHoverTooltip} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} /><Legend {...legendProps} />
            {seriesWithData.map((s, i) => (
              i === lastIdx
                ? <Line key={s.id} hide={isHidden(s.id)} type={lineType} dataKey={`p${s.id}`} name={s.name} stroke={colorFor(s, i)} strokeWidth={2} dot={activePeriod === 'D' ? makeDot(cfgFor(s, i).shape, colorFor(s, i)) : false} activeDot={makeDot(cfgFor(s, i).shape, colorFor(s, i))} isAnimationActive={false} connectNulls={false} />
                : <Bar key={s.id} hide={isHidden(s.id)} dataKey={`p${s.id}`} name={s.name} fill={colorFor(s, i)} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      )
    }
    if (isWindRose) {
      if (windRoseLoading) {
        return (
          <div className="flex items-center justify-center h-full text-sm text-gray-500 gap-2">
            <Loader className="w-4 h-4 animate-spin" /> Loading wind rose data…
          </div>
        )
      }
      if (!windRoseData) {
        return (
          <div className="flex items-center justify-center h-full text-sm text-gray-400 text-center px-6">
            Click the chart settings (⋮ → Chart Settings) to pick a device, Wind Direction, and Wind Speed parameter.
          </div>
        )
      }
      if (windRoseData.totalPairedSamples === 0) {
        return (
          <div className="flex items-center justify-center h-full text-sm text-gray-400">
            No paired wind observations in this period.
          </div>
        )
      }
      // Band colors follow whatever the user picked in the Key Colors section
      // of WindRoseEditorModal, falling back to the default palette — same
      // resolution the Overview dashboard's Wind Rose widget uses.
      const bandColors = WIND_SPEED_BANDS.map((b) => (windRoseKeyColors || {})[b.key] || b.defaultColor)
      const bandLabels = WIND_SPEED_BANDS.map((b) => b.label)
      return (
        <div className="w-full h-full flex flex-col items-center justify-center gap-3 overflow-auto p-2">
          <WindRoseChart
            sectors={windRoseData.sectors}
            bandColors={bandColors}
            bandLabels={bandLabels}
            fromDate={windRoseData.fromDate}
            toDate={windRoseData.toDate}
            size={320}
          />
          {/* Band legend + summary line — same as the Overview dashboard's
              Wind Rose widget (DashboardWidgets.jsx's WindRoseWidget). */}
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 text-[11px] text-gray-600 px-2">
            {WIND_SPEED_BANDS.map((b, i) => (
              <span key={b.key} className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                  style={{ backgroundColor: bandColors[i] }}
                />
                {b.label}
              </span>
            ))}
          </div>
          <p className="text-[10px] text-gray-400">
            {windRoseData.totalPairedSamples.toLocaleString()} paired observations
            {windRoseData.dominantSector ? ` · dominant: ${windRoseData.dominantSector}` : ''}
          </p>
        </div>
      )
    }
    if (isGauge) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart data={pieData} innerRadius="30%" outerRadius="100%" startAngle={180} endAngle={0}><RadialBar background dataKey="value" /><Tooltip formatter={tipFormatter} /><Legend /></RadialBarChart>
        </ResponsiveContainer>
      )
    }
    if (isMulti) {
      // Small-multiples layout — same visual as MultiChartWidget on the
      // Parameter Comparison dashboard: one mini LineChart per parameter,
      // stacked vertically, each with its own title and its own y-axis so
      // parameters in different units don't share a scale.
      //
      // Iterates over ALL selected parameters (effSeries), not just the
      // ones with data (seriesWithData) — a parameter with no points for
      // the selected range still gets its own slot, showing "No data
      // available" instead of silently disappearing from the layout.
      return (
        <div className="w-full h-full overflow-y-auto p-4 space-y-5">
          {effSeries.map((s, i) => {
            const cfg = cfgFor(s, i)
            const stroke = cfg.color
            const title = cfg.yTitle || (s.unit ? `${s.name} (${s.unit})` : s.name)
            if (s.points.length === 0) {
              return (
                <div key={s.id}>
                  <div className="text-xs mb-1" style={{ color: stroke }}>{title}</div>
                  <div
                    className="w-full flex items-center justify-center text-sm text-gray-400 border border-dashed border-gray-200 rounded"
                    style={{ height: 180 }}
                  >
                    No data available
                  </div>
                </div>
              )
            }
            // Plot against the shared `rows` union (this series' value under
            // p<id>, null where it has no sample) — NOT the series' own points.
            // The X-axis ticks (multiXTicks) come from that union, so a mini
            // chart plotting only its own subset would leave ticks unaligned /
            // crowded (e.g. the "week" axis). Using rows keeps every mini
            // chart's W/M/Y axis identical to the single Line chart's.
            const seriesKey = `p${s.id}`
            // Over a W/M/Y range there are far more raw samples (10-min
            // interval → 1000+ points) than pixels of chart width, so
            // drawing a dot at EVERY point makes them all merge into a
            // solid band. Thin them to ~150 evenly-spaced markers.
            const dotStep = Math.max(1, Math.ceil(rows.length / 150))
            const renderDot = (dotProps) => {
              if (dotProps.index % dotStep !== 0) return null
              const base = makeDot(cfg.shape, stroke)
              if (typeof base === 'function') return base(dotProps)
              if (!base) return null
              const { cx, cy } = dotProps
              if (cx == null || cy == null) return null
              return <circle key={`d-${s.id}-${dotProps.index}`} cx={cx} cy={cy} r={base.r} fill={base.fill} stroke={base.stroke} />
            }
            return (
              <div key={s.id}>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart
                    data={rows}
                    margin={{ top: 10, right: 12, left: 8, bottom: 5 }}
                    // All the small multiples share one sync group, so hovering
                    // any parameter shows the same instant on every stacked
                    // chart. Sync by x VALUE — each series can have a different
                    // number of samples, so index-matching would align the wrong
                    // instants.
                    syncId="da-multi-line"
                    syncMethod="value"
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#888' }} ticks={multiXTicks} tickFormatter={multiXTickFormatter} />
                    <YAxis tick={{ fontSize: 10, fill: '#888' }} width={72} domain={domainFor(cfg)} ticks={ticksFor(cfg)} allowDataOverflow>
                      <Label value={title} angle={-90} position="insideLeft" offset={8} style={{ fontSize: 11, fill: stroke, textAnchor: 'middle' }} />
                    </YAxis>
                    {/* Single-series mini chart: dataKey is "y", so format
                        straight from this series' name + unit. */}
                    <Tooltip
                      isAnimationActive={false}
                      wrapperStyle={{ transition: 'none' }}
                      content={(p) => (
                        <ChartHoverTooltip
                          {...p}
                          formatLabel={(l) => (l == null ? '' : String(l))}
                          describe={(entry) => {
                            const vv = fmtVal(entry?.value, Number.isFinite(s.precision) ? s.precision : 0)
                            return { name: s.name, valueText: s.unit ? `${vv} ${s.unit}` : `${vv}`, color: entry?.color || entry?.stroke || stroke }
                          }}
                        />
                      )}
                    />
                    {/* Always show dots here, regardless of the global 1-day
                        showDots cutoff — that cutoff exists to avoid clutter
                        when many series overlap on one shared axis, but each
                        Multi-Line mini-chart only ever plots a single trace,
                        so there's no such crowding even over multi-day
                        (W/M/Y) ranges. */}
                    <Line type={lineType} dataKey={seriesKey} stroke={stroke} strokeWidth={2} dot={activePeriod === 'D' ? renderDot : false} activeDot={makeDot(cfg.shape, stroke)} connectNulls={false} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )
          })}
          {/* Single X-axis title centred beneath the stacked mini-charts. They
              all share the same time scale, so one title at the bottom reads
              cleaner than repeating it per chart — and the old per-XAxis Label
              had no room to render in the 5px bottom margin, so it never
              appeared. */}
          {settings.xTitle && (
            <div className="text-center text-xs text-gray-600 pt-1 pb-2">{settings.xTitle}</div>
          )}
        </div>
      )
    }
    return withXTitle(
      <div className="relative w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={rows}
            onMouseDown={(e) => {
              if (e?.activeLabel == null) return
              setZoomDragStart(e.activeLabel); setZoomDragEnd(e.activeLabel)
            }}
            onMouseMove={(e) => {
              if (zoomDragStart == null || e?.activeLabel == null) return
              setZoomDragEnd(e.activeLabel)
            }}
            onMouseUp={() => {
              if (zoomDragStart == null || zoomDragEnd == null) { setZoomDragStart(null); setZoomDragEnd(null); return }
              if (zoomDragStart === zoomDragEnd) { setZoomDragStart(null); setZoomDragEnd(null); return }
              const lo = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                ? Math.min(zoomDragStart, zoomDragEnd) : zoomDragStart
              const hi = (typeof zoomDragStart === 'number' && typeof zoomDragEnd === 'number')
                ? Math.max(zoomDragStart, zoomDragEnd) : zoomDragEnd
              setZoomDomain([lo, hi])
              setZoomDragStart(null); setZoomDragEnd(null)
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            {xAxis({ domain: zoomDomain || undefined, allowDataOverflow: !!zoomDomain })}
            {/* One <YAxis> per parameter — each parameter's data is in its
                own units/range, so a shared axis is meaningless. Axes
                alternate left/right; stroke matches the line color so
                users can tell at a glance which axis belongs to which
                trace. When the user set an explicit yMin/yMax in Chart
                Settings, we honour it for every axis; otherwise each
                axis auto-scales to its own data. */}
            {/* Render a Y-axis and a Line for EVERY selected parameter, not
                only those with data, so a parameter that has no data in the
                window still keeps its axis and its legend title. */}
            {axisGroups.map((group, gIdx) => {
              const { s, i } = group.members[0]
              const shared = group.members.length > 1
              const cfg = cfgFor(s, i)
              // A shared axis carries traces of different colours, so tint it
              // neutrally instead of adopting one member's colour.
              const axisColor = shared ? '#6B7280' : cfg.color
              // Alternate by the GROUP's ordinal (0→left, 1→right, …) so two
              // groups can't share a side when selection indices skip (the
              // firstIdx parity bug put both axes on the same side).
              const side = gIdx % 2 === 0 ? 'left' : 'right'
              // Only drop the axis once every series on it is hidden.
              const allHidden = group.members.every((m) => isHidden(m.s.id))
              return (
              <YAxis
                key={group.axisId}
                yAxisId={group.axisId}
                hide={allHidden}
                orientation={side}
                tick={{ fontSize: 11, fill: axisColor }}
                stroke={axisColor}
                domain={domainFor(cfg)}
                allowDataOverflow={true}
                ticks={ticksFor(cfg)}
                // 60 (was 56) so the tick numbers and the rotated axis title
                // both fit inside the band without touching.
                width={60}
              >
                {/* offset keeps this title clear of the NEIGHBOURING axis's tick
                    numbers — with no offset it sat ~13px from them, so a second
                    axis's title looked wedged between the two scales.
                    textAnchor:middle centres the rotated text along the axis
                    instead of anchoring it at one end, which is why the titles
                    previously sat at different heights. */}
                {/* Axis title mirrors the Overview Line Chart widget exactly:
                    the UNIT (same `unit` field from graph-data). Parameter names
                    live in the legend, so the axis stays uncluttered and the
                    title is short enough not to clip. A custom per-series title
                    from Chart Settings still wins; render nothing when neither is
                    set (same as Overview). */}
                {(cfg.yTitle || s.unit) && (
                  <Label
                    value={cfg.yTitle || s.unit}
                    angle={-90}
                    position={side === 'left' ? 'insideLeft' : 'insideRight'}
                    offset={14}
                    style={{ fontSize: 10, fill: axisColor, fontWeight: 600, textAnchor: 'middle' }}
                  />
                )}
              </YAxis>
            )})}
            {/* Hovering a legend entry highlights that parameter's line and dims
                the rest. The payload carries the Line dataKey ("p<id>"). */}
            <Tooltip content={renderHoverTooltip} isAnimationActive={false} wrapperStyle={{ transition: 'none' }} />
            <Legend {...legendProps} />
            {effSeries.map((s, i) => {
              const isHovered = hoveredParamId === String(s.id)
              const dimmed = hoveredParamId != null && !isHovered
              return (
                <Line key={s.id} yAxisId={axisIdOf(s.id)} hide={isHidden(s.id)} type={lineType} dataKey={`p${s.id}`} name={s.name}
                  stroke={colorFor(s, i)} strokeWidth={isHovered ? 4 : 2} strokeOpacity={dimmed ? 0.2 : 1}
                  // Dimmed series drop their markers too — strokeOpacity fades
                  // only the line, leaving the dots at full strength.
                  dot={dimmed ? false
                    : (activePeriod === 'D' ? makeDot(cfgFor(s, i).shape, colorFor(s, i)) : false)}
                  activeDot={dimmed ? false : makeDot(cfgFor(s, i).shape, colorFor(s, i))}
                  isAnimationActive={false} connectNulls={false} />
              )
            })}
            {zoomDragStart != null && zoomDragEnd != null && zoomDragStart !== zoomDragEnd && (
              <ReferenceArea x1={zoomDragStart} x2={zoomDragEnd} strokeOpacity={0.3} fill="#358ECD" fillOpacity={0.15} />
            )}
          </LineChart>
        </ResponsiveContainer>
        {zoomDomain && (
          <button
            onClick={() => { setZoomDomain(null); setZoomDragStart(null); setZoomDragEnd(null) }}
            className="absolute top-2 right-2 text-xs bg-white border border-gray-300 text-gray-600 px-2 py-0.5 rounded hover:bg-gray-50 shadow-sm z-10"
          >
            Reset Zoom
          </button>
        )}
      </div>
    )
  }


  // Quick Report → a PDF matching the full Report page's layout (see
  // ReportView.jsx's buildReportPdf): dated/titled header + Statistical
  // Summary table on page 1, the chart image under a "Plots" heading on
  // page 2, and a pivoted Data Table on page 3. Same jsPDF + jspdf-autotable
  // styling (fonts, margins, table theme) so the two PDFs read as one system.
  const quickReport = async () => {
    setMenuOpen(false); setBusy('report')
    try {
      const { jsPDF } = await import('jspdf')
      const html2canvas = (await import('html2canvas')).default
      const { autoTable } = await import('jspdf-autotable')

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      const MARGIN = 36

      const now = new Date()
      const fmtDate = (d) => {
        try { return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, year: 'numeric', month: 'long', day: 'numeric' }).format(d) }
        catch { return d.toDateString() }
      }
      const fmtTime = (d) => {
        try { return new Intl.DateTimeFormat('en-US', { timeZone: customerTz, hour: '2-digit', minute: '2-digit', hour12: true }).format(d) }
        catch { return d.toLocaleTimeString() }
      }
      const fmtStamp = (x) => {
        const d = new Date(x)
        if (isNaN(d.getTime())) return String(x)
        try {
          const s = new Intl.DateTimeFormat('en-US', {
            timeZone: customerTz, year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
          }).format(d)
          return s.replace(/^(\d{2})\/(\d{2})\/(\d{4}),?\s*/, '$1-$2-$3 ')
        } catch { return d.toISOString().slice(0, 19).replace('T', ' ') }
      }
      const num = (v) => (v == null || !Number.isFinite(Number(v))) ? '—' : Number(v).toFixed(2)

      // ── Page 1: Header + Statistical Summary ──
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(64)
      doc.text(fmtDate(now), MARGIN, MARGIN + 4)
      doc.text(fmtTime(now), MARGIN, MARGIN + 16)

      let y = MARGIN + 4
      doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.setTextColor(20)
      doc.text(settings.chartTitle || 'Data Analytics Report', pageW / 2, y + 4, { align: 'center' })
      y += 24 + 18

      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
      doc.text('Statistical Summary', pageW / 2, y, { align: 'center' })
      y += 10

      const statsRows = seriesWithData.map((s) => {
        let min = Infinity, minAt = null, max = -Infinity, maxAt = null, sum = 0, n = 0
        const values = []
        s.points.forEach((p) => {
          if (p.y == null) return   // hidden-status gap — not a real reading
          const v = Number(p.y)
          if (!Number.isFinite(v)) return
          values.push(v)
          if (v < min) { min = v; minAt = p.x }
          if (v > max) { max = v; maxAt = p.x }
          sum += v; n += 1
        })
        if (!n) return { s, count: 0, min: null, minAt: null, max: null, maxAt: null, mean: null, median: null, std: null }
        const meanV = sum / n
        const sorted = [...values].sort((a, b) => a - b)
        const median = n % 2 === 0 ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2 : sorted[Math.floor(n / 2)]
        const variance = values.reduce((acc, v) => acc + (v - meanV) ** 2, 0) / n
        return { s, count: n, min, minAt, max, maxAt, mean: meanV, median, std: Math.sqrt(variance) }
      })

      autoTable(doc, {
        startY: y + 6,
        margin: { left: MARGIN, right: MARGIN },
        styles:   { fontSize: 8, cellPadding: 3, valign: 'top', overflow: 'linebreak', textColor: 30 },
        headStyles: { fillColor: [245, 245, 245], textColor: 40, halign: 'center', fontStyle: 'bold' },
        theme: 'grid',
        head: [['Sites', 'Device', 'Sensors', 'Parameters', 'Samples', 'Min', 'Max', 'Mean', 'Median', 'Std']],
        body: statsRows.map((r) => [
          r.s.siteName   || '—',
          r.s.deviceName || '—',
          '—',
          `${r.s.name || ''}${r.s.unit ? ` (${r.s.unit})` : ''}`,
          String(r.count ?? 0),
          r.min != null ? `${num(r.min)}\n${fmtStamp(r.minAt)}` : '—',
          r.max != null ? `${num(r.max)}\n${fmtStamp(r.maxAt)}` : '—',
          num(r.mean),
          num(r.median),
          num(r.std),
        ]),
      })

      // ── Page 2: Plots ──
      doc.addPage()
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
      doc.text('Plots', pageW / 2, MARGIN + 10, { align: 'center' })
      doc.setFont('helvetica', 'normal'); doc.setFontSize(10); doc.setTextColor(70)
      doc.text('Selected Parameters', pageW / 2, MARGIN + 30, { align: 'center' })

      // Exclude Recharts' hover-tooltip wrapper AND its cursor/crosshair
      // guideline from the capture — if the cursor happened to be sitting
      // over the chart when Quick Report was clicked, both would otherwise
      // get baked into the PDF. These are two separate elements: the
      // tooltip popup box (recharts-tooltip-wrapper) and the vertical
      // reference line Recharts draws under it (recharts-tooltip-cursor) —
      // excluding only the wrapper still left the stray vertical line behind.
      const canvas = await html2canvas(chartRef.current, {
        scale: 2,
        backgroundColor: '#fff',
        ignoreElements: (el) =>
          el.classList?.contains('recharts-tooltip-wrapper') ||
          el.classList?.contains('recharts-tooltip-cursor'),
      })
      const img = canvas.toDataURL('image/png')
      const availW = pageW - MARGIN * 2
      const ih = canvas.height * (availW / canvas.width)
      const cappedIh = Math.min(ih, pageH - MARGIN * 2 - 60)
      const cappedIw = cappedIh < ih ? canvas.width * (cappedIh / canvas.height) : availW
      const xOffset = MARGIN + (availW - cappedIw) / 2
      doc.addImage(img, 'PNG', xOffset, MARGIN + 50, cappedIw, cappedIh)

      // ── Page 3: Data Table ──
      doc.addPage()
      doc.setFont('helvetica', 'bold'); doc.setFontSize(12); doc.setTextColor(20)
      doc.text('Data Table', pageW / 2, MARGIN + 10, { align: 'center' })

      const tzLabel = (() => {
        try {
          const opt = new Intl.DateTimeFormat('en-US', { timeZone: customerTz, timeZoneName: 'longOffset' })
            .formatToParts(new Date()).find((p) => p.type === 'timeZoneName')?.value || ''
          return `Time ${customerTz} ${opt || ''}`.trim()
        } catch { return 'Time' }
      })()

      const bodyHeaderRows = [
        ['Site',      ...seriesWithData.map((s) => s.siteName   || '—')],
        ['Device',    ...seriesWithData.map((s) => s.deviceName || '—')],
        ['Sensor',    ...seriesWithData.map(() => '—')],
        ['Parameter', ...seriesWithData.map((s) => s.name       || '')],
        ['',          ...seriesWithData.map((s) => s.unit       || '')],
      ]
      const dataBody = rows.map((row) => [
        fmtStamp(row.x),
        ...seriesWithData.map((s) => {
          const v = row[`p${s.id}`]
          return typeof v === 'number' ? v.toFixed(2) : '—'
        }),
      ])

      autoTable(doc, {
        startY: MARGIN + 26,
        margin: { left: MARGIN, right: MARGIN, top: MARGIN + 26, bottom: MARGIN },
        styles: { fontSize: 8, cellPadding: 2.5, textColor: 40, halign: 'right' },
        headStyles: { fillColor: [245, 245, 245], textColor: 30, halign: 'center', fontStyle: 'bold' },
        columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
        theme: 'grid',
        head: [[tzLabel, ...seriesWithData.map(() => '')]],
        body: [...bodyHeaderRows, ...dataBody],
      })

      doc.save(`${settings.chartTitle || chartType || 'report'}.pdf`)
    } catch (e) { alert('Could not generate report: ' + e.message) }
    finally { setBusy('') }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      {/* 16:9 dialog. Sized from the WIDTH so the ratio always holds — pairing
          `aspect-ratio` with a max-height would let a short viewport clamp the
          height and silently break the ratio. The 160vh term is what keeps it
          on screen: 160vh x 9/16 = 90vh, so height can never exceed 90vh, and
          92vw takes over on narrow/portrait screens. */}
      <div
        className="bg-white rounded-lg overflow-hidden shadow-lg flex flex-col"
        style={isMobile
          ? { width: '94vw', height: '84vh' }        // phone: large portrait sheet with margins
          : { width: 'min(92vw, 160vh)', aspectRatio: '16 / 9' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header: title + 3-dot menu + close */}
        <div className="flex items-center justify-between px-4 py-3 md:px-6 md:py-6 border-b border-gray-200 relative">
          <h2 className="common-head25 text-lg font-bold text-[#4D4D4D]">{isWindRose ? (windRoseWidgetName || 'Wind Rose') : (settings.chartTitle || `Title`)}</h2>
          <div className="flex items-center gap-2">
            {/* In-popup period switcher — switching updates the fetched data,
                so the X and Y axes re-scale live for every chart type. */}
            {onPeriodChange && (
              <div className="flex items-center gap-1 border border-gray-300 rounded px-1 py-0.5 mr-1">
                {['D', 'W', 'M', 'Y'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => onPeriodChange(p)}
                    className={`px-2 py-0.5 text-xs rounded transition ${activePeriod === p ? 'bg-gray-700 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                  >{p}</button>
                ))}
              </div>
            )}
            <div className="relative">
              <button onClick={() => setMenuOpen((o) => !o)} title="Options" className="text-gray-500 hover:text-gray-800 p-1 rounded hover:bg-gray-100">
                <MoreVertical className="w-5 h-5" />
              </button>
              {menuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 w-48 py-1 text-sm">
                  <button
                    onClick={() => {
                      setMenuOpen(false)
                      // Wind Rose is configured via its own device/parameter
                      // picker (WindRoseEditorModal, owned by VisualizeTab) —
                      // not the generic per-series Display/Titles/Data modal.
                      if (isWindRose) onEditWindRose?.()
                      else setSettingsOpen(true)
                    }}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50"
                  >Chart Settings</button>
                  <div className="border-t border-gray-100 my-1" />
                  <button onClick={quickReport} className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50">Quick Report</button>
                </div>
              )}
            </div>
            <button onClick={onClose} title="Close" aria-label="Close" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
          </div>
        </div>

        <div ref={chartRef} className="flex-1 p-4 min-h-0 bg-white">{renderChart()}</div>
      </div>

      {settingsOpen && (
        <ChartSettingsModal
          series={series}
          chartType={chartType}
          colorFor={colorFor}
          initial={settings}
          onCancel={() => setSettingsOpen(false)}
          onSave={(next) => { setSettings(next); saveSettings(next); setSettingsOpen(false) }}
        />
      )}
    </div>
  )
}

// Module-scope so they are NOT recreated each render (recreating a component
// inside render remounts its inputs and steals focus after one keystroke).
const SETTINGS_TABS = ['Display', 'Titles', 'Data']
const SETTINGS_INPUT = 'w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-700 focus:outline-none focus:border-[#358ECD]'
const Field = ({ label, children }) => (
  <div className="mb-4"><label className="block text-sm text-gray-700 mb-1">{label}</label>{children}</div>
)

// ── Chart Settings tabbed modal (Y-Axis / Titles / Colors / Data) ──
function ChartSettingsModal({ series, chartType, colorFor, initial, onCancel, onSave }) {
  const [tab, setTab] = useState('Display')
  const [draft, setDraft] = useState(initial)
  // Point Shape only applies to chart types that draw point markers (lines +
  // area + the combo's line). Bar/column/pie/gauge/wind-rose have no points,
  // so hide the control there instead of showing one that silently does
  // nothing when changed.
  const usesPointShape = ['line', 'multi-line', 'combo', 'area'].includes(chartType)
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }))
  // Per-series field setter (Display tab): color / pointShape / yTitle / yMin / yMax / ySteps.
  const setSeries = (id, field, value) => setDraft((d) => ({
    ...d,
    series: { ...(d.series || {}), [id]: { ...(d.series?.[id] || {}), [field]: value } },
  }))
  const sVal = (id, field, fallback = '') => (draft.series?.[id]?.[field] ?? fallback)
  const TABS = SETTINGS_TABS
  const inputCls = SETTINGS_INPUT
  const sLabel = (s) => [s.siteName, s.deviceName, s.unit ? `${s.name} (${s.unit})` : s.name].filter(Boolean).join(' | ')

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-[1100] p-4" onClick={onCancel}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="common-head25 text-lg font-bold text-[#4D4D4D]">Chart Settings</h3>
        </div>
        <div className="flex gap-6 px-6 pt-6">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} className={`pb-2 common-size15 font-semibold text-sm border-b-4 transition ${tab === t ? 'border-[#358ECD] text-[#4D4D4D]' : 'border-transparent text-[#4D4D4D] hover:text-gray-800'}`}>{t}</button>
          ))}
        </div>

        <div className="px-6 py-6 overflow-y-auto flex-1 min-h-[240px]">
          {/* Display — one block per series: Line Color + Point Shape + Y-Axis */}
          {tab === 'Display' && (
            series.length === 0
              ? <p className="text-sm text-gray-400">No parameters selected.</p>
              : series.map((s, i) => {
                  const color = draft.series?.[s.id]?.color || colorFor(s, i)
                  const shape = draft.series?.[s.id]?.pointShape || 'Circle'
                  return (
                    <div key={s.id} className="mb-5 pb-5 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                      <p className="common-size15 text-sm font-bold text-[#4D4D4D] mb-3">{sLabel(s)}</p>
                      <div className="flex items-center gap-6 flex-wrap mb-4">
                        <div className="flex items-center gap-2">
                          <label className="common-size15 text-xs font-semibold text-[#4D4D4D]">Line Color</label>
                          <label className="flex items-center gap-1 border border-gray-200 rounded-md px-1.5 py-1 cursor-pointer hover:bg-gray-50" title={color}>
                            <ShapeSwatch shape={usesPointShape ? shape : 'Square'} color={color} />
                            <ChevronDown className="w-3 h-3 text-gray-400" />
                            <input type="color" value={color} onChange={(e) => setSeries(s.id, 'color', e.target.value)} className="sr-only" />
                          </label>
                        </div>
                        {usesPointShape && (
                          <div className="flex items-center gap-2">
                            <label className="common-size15 text-xs font-semibold text-[#4D4D4D]">Point Shape</label>
                            <select value={shape} onChange={(e) => setSeries(s.id, 'pointShape', e.target.value)} className="brdr-cstm border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-[#358ECD]">
                              {POINT_SHAPES.map((sh) => <option key={sh}>{sh}</option>)}
                            </select>
                          </div>
                        )}
                        {/* Combine (Fix #1): give two parameters the same number to
                            share one Y-Axis (e.g. two turbidity probes in NTU/FNU
                            on a single scale). "Auto" keeps each on its own axis.
                            Only the default multi-axis Line chart honours this. */}
                        {chartType === 'line' && (
                          <div className="flex items-center gap-2">
                            <label className="common-size15 text-xs font-semibold text-[#4D4D4D]" title="Put parameters on the same Y-Axis number to share one scale">Combine</label>
                            <select
                              value={sVal(s.id, 'yAxisGroup') || 'Auto'}
                              onChange={(e) => setSeries(s.id, 'yAxisGroup', e.target.value)}
                              title="Put parameters on the same Y-Axis number to share one scale"
                              className="brdr-cstm border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-[#358ECD]"
                            >
                              {['Auto', '1', '2', '3', '4'].map((g) => <option key={g} value={g}>{g}</option>)}
                            </select>
                          </div>
                        )}
                      </div>
                      <label className="block common-size15 text-xs font-semibold text-[#4D4D4D] mb-2">Y-Axis</label>
                      <input className={inputCls + ' mb-4 rounded-md'} placeholder="Default Title" value={sVal(s.id, 'yTitle')} onChange={(e) => setSeries(s.id, 'yTitle', e.target.value)} />
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5"><span className="common-size15 text-xs font-semibold text-[#4D4D4D]">Min</span><input className={inputCls + ' rounded-md'} placeholder="Minimum #" value={sVal(s.id, 'yMin')} onChange={(e) => setSeries(s.id, 'yMin', e.target.value)} /></div>
                        <div className="flex items-center gap-1.5"><span className="common-size15 text-xs font-semibold text-[#4D4D4D]">Max</span><input className={inputCls + ' rounded-md'} placeholder="Maximum #" value={sVal(s.id, 'yMax')} onChange={(e) => setSeries(s.id, 'yMax', e.target.value)} /></div>
                        <div className="flex items-center gap-1.5"><span className="common-size15 text-xs font-semibold text-[#4D4D4D]">Steps</span><input className={inputCls + ' rounded-md'} placeholder="Default" value={sVal(s.id, 'ySteps')} onChange={(e) => setSeries(s.id, 'ySteps', e.target.value)} /></div>
                      </div>
                    </div>
                  )
                })
          )}
          {tab === 'Titles' && (<>
            <Field label="Chart Title"><input className={inputCls} placeholder="Default" value={draft.chartTitle} onChange={(e) => set({ chartTitle: e.target.value })} /></Field>
            <Field label="X-Axis Title"><input className={inputCls} placeholder="Default" value={draft.xTitle} onChange={(e) => set({ xTitle: e.target.value })} /></Field>
          </>)}
          {tab === 'Data' && (<>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={draft.smoothLine} onChange={(e) => set({ smoothLine: e.target.checked })} /> Smooth Line
            </label>
            <div className={`pl-6 mb-4 ${draft.smoothLine ? '' : 'opacity-40 pointer-events-none'}`}>
              <select className={inputCls + ' mb-2'} value={draft.smoothMethod} onChange={(e) => set({ smoothMethod: e.target.value })}>
                <option value="moving-average">Moving Average</option>
              </select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">Averaging Window</span>
                <input type="number" min="1" className={inputCls} placeholder="Number of Points to Average" value={draft.avgWindow} onChange={(e) => set({ avgWindow: e.target.value })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input type="checkbox" checked={draft.removeSpikes} onChange={(e) => set({ removeSpikes: e.target.checked })} /> Remove Spikes
            </label>
            <div className={`pl-6 ${draft.removeSpikes ? '' : 'opacity-40 pointer-events-none'}`}>
              <select className={inputCls + ' mb-2'} value="std-dev" readOnly><option value="std-dev">Standard Deviation</option></select>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 whitespace-nowrap">Increment Deviations</span>
                <input type="number" min="0" step="0.5" className={inputCls} placeholder="Number of Standard Deviations" value={draft.stdDev} onChange={(e) => set({ stdDev: e.target.value })} />
              </div>
            </div>
          </>)}
        </div>

        {/* Footer — full-width SAVE + Cancel link (matches the XD) */}
        <div className="px-6 py-5 border-t border-gray-100">
          <button onClick={() => onSave(draft)} className="w-full py-2.5 bg-[#358ECD] hover:bg-[#2d7ab5] text-white text-sm font-bold tracking-wide rounded-md transition">SAVE</button>
          <button onClick={onCancel} className="block mx-auto mt-2.5 text-sm text-[#358ECD] hover:underline">Cancel</button>
        </div>
      </div>
    </div>
  )
}
